/*
 * eri-accordion.js
 * .accordion（商品ページ 説明/ストーリー/ケア, Collapsible content セクション）の
 * <details> 開閉を Web Animations API でピクセル等速アニメーションする。
 *
 * CSS ネイティブ補間（::details-content の grid / block-size）は方向ごとに始端・終端で
 * 引っかかるため、高さアニメは JS に一本化する。開く時・閉じる時とも details 要素自体の
 * height を実測ピクセルで補間するので両方向なめらかに動く。
 *
 * - 連打中はアニメをキャンセルして逆方向へ滑らかに切り替える。
 * - キャレットは .is-open クラス駆動（CSS 側）で、アニメ開始と同時に回転させる。
 * - prefers-reduced-motion: reduce の場合は強化せず、Dawn 既定の即時開閉に委ねる。
 * - 対象外: ナビ/カート/絞り込み等の details（独自 JS 制御のため .accordion 限定）。
 */
(() => {
  const DURATION = 380;
  const EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class EriAccordion {
    /** @param {HTMLDetailsElement} details */
    constructor(details) {
      this.details = details;
      this.summary = details.querySelector('summary');
      this.content = this.summary?.nextElementSibling ?? null;
      /** @type {Animation | null} */
      this.animation = null;
      this.isClosing = false;
      this.isExpanding = false;

      if (!this.summary || !this.content) return;

      details.classList.add('eri-enh');
      details.classList.toggle('is-open', details.open);
      this.summary.addEventListener('click', (event) => this.onClick(event));
    }

    /** @param {MouseEvent} event */
    onClick(event) {
      event.preventDefault();
      this.details.style.overflow = 'hidden';

      if (this.isClosing || !this.details.open) {
        this.open();
      } else if (this.isExpanding || this.details.open) {
        this.shrink();
      }
    }

    open() {
      this.details.classList.add('is-open');
      this.details.style.height = `${this.details.offsetHeight}px`;
      this.details.open = true;
      window.requestAnimationFrame(() => this.expand());
    }

    expand() {
      const startHeight = `${this.details.offsetHeight}px`;
      // 開いた状態の自然な高さを実測（同フレーム内の読み書きなのでチラつかない）
      this.details.style.height = 'auto';
      const endHeight = `${this.details.offsetHeight}px`;
      this.details.style.height = startHeight;
      this.run(startHeight, endHeight, true);
    }

    shrink() {
      this.details.classList.remove('is-open');
      const startHeight = `${this.details.offsetHeight}px`;
      const endHeight = `${this.summary.offsetHeight}px`;
      this.run(startHeight, endHeight, false);
    }

    /**
     * @param {string} startHeight
     * @param {string} endHeight
     * @param {boolean} opening
     */
    run(startHeight, endHeight, opening) {
      this.isExpanding = opening;
      this.isClosing = !opening;

      if (this.animation) this.animation.cancel();

      this.animation = this.details.animate(
        { height: [startHeight, endHeight] },
        { duration: DURATION, easing: EASING }
      );
      this.animation.onfinish = () => this.onFinish(opening);
      this.animation.oncancel = () => {
        this.isExpanding = false;
        this.isClosing = false;
      };
    }

    /** @param {boolean} open */
    onFinish(open) {
      this.details.open = open;
      this.details.classList.toggle('is-open', open);
      this.details.style.height = '';
      this.details.style.overflow = '';
      this.animation = null;
      this.isExpanding = false;
      this.isClosing = false;
    }
  }

  const init = () => {
    if (reduceMotion.matches) return; // モーション抑制時は Dawn 既定（即時開閉）に委ねる
    document.querySelectorAll('.accordion > details').forEach((details) => {
      if (details.dataset.eriAccordion) return;
      details.dataset.eriAccordion = 'true';
      new EriAccordion(details);
    });
  };

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
